import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSave, FiAlertCircle } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import "../styles/Dashboard.css";
import "../styles/Modal.css";

export default function AdvancedPixelsEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);
  
  const [pixels, setPixels] = useState({
    facebook: "",
    google: "",
    x: ""
  });
  
  const fetchLink = useCallback(async () => {
    try {
      const data = await api.getLink(id);
      setLink(data);
      
      try {
        if (data.pixel_ids) {
          const parsed = JSON.parse(data.pixel_ids);
          setPixels({
            facebook: parsed.facebook || "",
            google: parsed.google || "",
            x: parsed.x || ""
          });
        }
      } catch (e) {}
      
    } catch (err) {
      addToast("Failed to load link", "error");
      navigate("/advanced/pixels");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    document.title = "Configure Pixels - LinkSquire";
    fetchLink();
  }, [fetchLink]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanPixels = {
        facebook: pixels.facebook.trim(),
        google: pixels.google.trim(),
        x: pixels.x.trim()
      };
      
      await api.updateLink(id, {
        pixels_enabled: true,
        pixel_ids: JSON.stringify(cleanPixels)
      });
      addToast("Pixel configuration saved", "success");
      navigate("/advanced/pixels");
    } catch (err) {
      addToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable tracking pixels for this link?")) return;
    
    setSaving(true);
    try {
      await api.updateLink(id, { pixels_enabled: false });
      addToast("Pixels disabled", "success");
      navigate("/advanced/pixels");
    } catch (err) {
      addToast(err.message || "Failed to disable", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn-sm" onClick={() => navigate("/advanced/pixels")} style={{ padding: '6px', background: 'transparent', border: '1px solid var(--border)' }}>
            <FiArrowLeft />
          </button>
          <h1 style={{ fontSize: '20px' }}>Configure Retargeting Pixels</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-sm btn-sm-danger" onClick={handleDisable} disabled={saving}>
            {saving ? <span className="spinner-sm dark" /> : null} Disable Pixels
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-sm" /> Saving...</> : <><FiSave /> Save Configuration</>}
          </button>
        </div>
      </div>
      
      <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 600 }}>Editing Link</div>
        <div style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {link.title || link.short_code} 
          <a href={link.short_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 400, textDecoration: 'none' }}>{link.short_url}</a>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '16px', background: '#e0f2fe', color: '#0369a1', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', lineHeight: 1.5 }}>
        <FiAlertCircle style={{ marginTop: '2px', flexShrink: 0 }} />
        <div>
          <strong>How this works:</strong> When users click this link, they will be briefly routed through a transition page for 2 seconds to allow your tracking scripts to load and fire before redirecting them to the final destination.
        </div>
      </div>

      <div className="links-section" style={{ padding: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '4px' }}>Tracking IDs</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Enter the ID for any platform you want to fire when a user clicks this link.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px' }}>
          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Facebook / Meta Pixel ID</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>Format: 123456789012345</span>
            </label>
            <input 
              type="text" 
              value={pixels.facebook} 
              onChange={(e) => setPixels({...pixels, facebook: e.target.value})} 
              placeholder="e.g. 123456789012345"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Google Analytics Measurement ID</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>Format: G-XXXXXXXXXX</span>
            </label>
            <input 
              type="text" 
              value={pixels.google} 
              onChange={(e) => setPixels({...pixels, google: e.target.value})} 
              placeholder="e.g. G-ABC123XYZ"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>X (Twitter) Pixel ID</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>Format: abc12</span>
            </label>
            <input 
              type="text" 
              value={pixels.x} 
              onChange={(e) => setPixels({...pixels, x: e.target.value})} 
              placeholder="e.g. abc12"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
